import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, MessageSquare, FileText, Users, 
  PenTool, Building2, BarChart3, Shield, 
  CheckCircle2, ChevronRight, Calendar, Clock,
  Mail, Phone, Target, Briefcase, TrendingUp
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';


const InvestmentProcessPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Investment process steps
  const processSteps = [
    {
      step: 1,
      title: 'Initial Inquiry',
      subtitle: 'Investor Qualification',
      icon: MessageSquare,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      textColor: 'text-blue-400',
      description: 'Submit an inquiry through our contact form or schedule a call. Our team will verify your investor status and understand your investment objectives.',
      details: [
        'Complete investor inquiry form',
        'investor verification',
        'Initial objectives discussion'
      ],
      duration: '1-2 days'
    },
    {
      step: 2,
      title: 'Materials Review',
      subtitle: 'Due Diligence',
      icon: FileText,
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      textColor: 'text-emerald-400',
      description: 'Access our Private Placement Memorandum, track record, and current investment pipeline. Review fund terms, fees, and historical performance data.',
      details: [
        'Private Placement Memorandum (PPM)',
        'Track record & case studies',
        'Current pipeline overview'
      ],
      duration: '3-5 days'
    },
    {
      step: 3,
      title: 'Consultation',
      subtitle: 'Strategy Discussion',
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      textColor: 'text-purple-400',
      description: 'Meet directly with our fund manager to discuss investment strategy, risk tolerance, and how our value-add approach aligns with your portfolio goals.',
      details: [
        'One-on-one with fund manager',
        'Strategy alignment discussion',
        'Risk tolerance assessment'
      ],
      duration: '30-60 min call'
    },
    {
      step: 4,
      title: 'Subscription',
      subtitle: 'Capital Commitment',
      icon: PenTool,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      textColor: 'text-amber-400',
      description: 'Complete subscription documents and finalize your capital commitment. Our team guides you through each document to ensure a smooth process.',
      details: [
        'Subscription agreement execution',
        'Capital commitment confirmation',
        'Wire instructions provided'
      ],
      duration: '2-3 days'
    },
    {
      step: 5,
      title: 'Capital Deployment',
      subtitle: 'Asset Acquisition',
      icon: Building2,
      color: 'from-rose-500 to-rose-600',
      bgColor: 'bg-rose-500/10',
      borderColor: 'border-rose-500/30',
      textColor: 'text-rose-400',
      description: 'Your capital is deployed into value-add real estate acquisitions. We source, underwrite, and acquire properties aligned with our investment thesis.',
      details: [
        'Capital called as deals close',
        'Acquisition notifications',
        'Property-level updates'
      ],
      duration: 'Ongoing'
    },
    {
      step: 6,
      title: 'Reporting & Distributions',
      subtitle: 'Ongoing Updates',
      icon: BarChart3,
      color: 'from-cyan-500 to-cyan-600',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      textColor: 'text-cyan-400',
      description: 'Receive quarterly performance reports, annual K-1 tax documents, and cash distributions. Access your investor portal for real-time updates.',
      details: [
        'Quarterly financial reports',
        'Cash flow distributions',
        'Annual K-1 tax documents'
      ],
      duration: 'Quarterly'
    }
  ];

  // Risk disclosures
  const riskPoints = [
    'All investments involve risk, including potential loss of principal',
    'Past performance is not indicative of future results',
    'Real estate investments are illiquid during the hold period',
    'Returns are not guaranteed and subject to market conditions',
    'The PPM governs all investment terms and disclosures'
  ];

  return (
    <>
      <Helmet>
        <title>How to Invest | Real Estate Investment Process | ASAP Capital</title>
        <meta 
          name="description" 
          content="Learn the step-by-step investment process at ASAP Capital, from initial inquiry to funding and ongoing investor reporting for investors." 
        />
        <meta name="keywords" content="real estate investment process, how to invest in real estate funds, investor investments, private equity investing process" />
        <link rel="canonical" href="/investment-process" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-28 lg:pt-44 lg:pb-36 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/3 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl mx-auto text-center"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">How to Invest</span>
                </motion.div>

                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.3] lg:leading-[1.35] mb-6"
                  variants={itemVariants}
                >
                  Our{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Investment Process
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p 
                  className="text-base lg:text-lg text-white/55 leading-relaxed mb-12 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  A step-by-step guide from initial inquiry to funding and ongoing reporting. 
                  Our transparent process is designed for investors seeking 
                  institutional-quality real estate investments.
                </motion.p>

                {/* CTA buttons */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link 
                    to="/contact"
                    
                    
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2 shadow-lg shadow-[#D4AF37]/20">
                      Start Your Journey
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      View Fund Terms
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROCESS OVERVIEW ==================== */}
          <section className="py-16 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                {/* Quick overview stats */}
                <div className="grid grid-cols-3 gap-6 mb-16">
                  <motion.div 
                    className="text-center"
                    variants={itemVariants}
                  >
                    <p className="text-3xl lg:text-4xl font-bold text-[#D4AF37] mb-2">6</p>
                    <p className="text-white/50 text-sm">Simple Steps</p>
                  </motion.div>
                  <motion.div 
                    className="text-center"
                    variants={itemVariants}
                  >
                    <p className="text-3xl lg:text-4xl font-bold text-white mb-2">2-3</p>
                    <p className="text-white/50 text-sm">Weeks to Fund</p>
                  </motion.div>
                  <motion.div 
                    className="text-center"
                    variants={itemVariants}
                  >
                    <p className="text-3xl lg:text-4xl font-bold text-white mb-2">24/7</p>
                    <p className="text-white/50 text-sm">Investor Portal</p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROCESS STEPS ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                {/* Section header */}
                <div className="text-center mb-16">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                    variants={itemVariants}
                  >
                    The Process
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-5"
                    variants={itemVariants}
                  >
                    From Inquiry to Funding
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl mx-auto"
                    variants={itemVariants}
                  >
                    Our streamlined investment process is designed to be clear, efficient, 
                    and investor-friendly at every stage.
                  </motion.p>
                </div>

                {/* Process steps timeline */}
                <div className="relative">
                  {/* Vertical line connector */}
                  <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4AF37]/30 via-[#D4AF37]/20 to-transparent transform -translate-x-1/2" />
                  
                  <div className="space-y-12 lg:space-y-0">
                    {processSteps.map((step, index) => (
                      <motion.div
                        key={step.step}
                        className={`relative ${index % 2 === 0 ? 'lg:pr-[52%]' : 'lg:pl-[52%]'}`}
                        initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.15, duration: 0.6 }}
                      >
                        {/* Center dot for timeline */}
                        <div className="hidden lg:flex absolute left-1/2 top-8 transform -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] items-center justify-center shadow-lg shadow-[#D4AF37]/30 z-10">
                          <span className="text-[#0A1628] font-bold">{step.step}</span>
                        </div>

                        {/* Card */}
                        <motion.div
                          className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 lg:p-8 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group relative overflow-hidden"
                          whileHover={{ y: -5, boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}
                        >
                          {/* Background gradient on hover */}
                          <div className={`absolute inset-0 bg-gradient-to-br ${step.bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                          
                          <div className="relative z-10">
                            {/* Mobile step number */}
                            <div className="lg:hidden flex items-center gap-3 mb-4">
                              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                                <span className="text-white font-bold text-sm">{step.step}</span>
                              </div>
                              <div className="flex items-center gap-2 text-white/40 text-sm">
                                <Clock className="h-4 w-4" />
                                <span>{step.duration}</span>
                              </div>
                            </div>

                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl ${step.bgColor} flex items-center justify-center border ${step.borderColor} group-hover:scale-110 transition-transform`}>
                                  <step.icon className={`h-6 w-6 ${step.textColor}`} />
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold text-white group-hover:text-[#D4AF37] transition-colors">
                                    {step.title}
                                  </h3>
                                  <p className="text-white/40 text-sm">{step.subtitle}</p>
                                </div>
                              </div>
                              <div className="hidden lg:flex items-center gap-2 text-white/40 text-sm">
                                <Clock className="h-4 w-4" />
                                <span>{step.duration}</span>
                              </div>
                            </div>

                            {/* Description */}
                            <p className="text-white/55 text-sm leading-relaxed mb-5">
                              {step.description}
                            </p>

                            {/* Details */}
                            <div className="space-y-2">
                              {step.details.map((detail, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <CheckCircle2 className={`h-4 w-4 ${step.textColor} flex-shrink-0`} />
                                  <span className="text-white/60">{detail}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== COMPLIANCE & RISK ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Content */}
                  <div>
                    <motion.span 
                      className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                      variants={itemVariants}
                    >
                      Important Information
                    </motion.span>
                    <motion.h2 
                      className="text-2xl lg:text-3xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Compliance & Risk Disclosure
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed mb-8"
                      variants={itemVariants}
                    >
                      We believe in complete transparency. Before investing, please review 
                      our Private Placement Memorandum for comprehensive disclosures.
                    </motion.p>

                    {/* Risk points */}
                    <motion.div className="space-y-3" variants={itemVariants}>
                      {riskPoints.map((point, index) => (
                        <motion.div
                          key={index}
                          className="flex items-start gap-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Shield className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/60 text-sm">{point}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Visual card */}
                  <motion.div variants={itemVariants}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute -top-8 -right-8 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl" />
                      
                      <div className="relative z-10 text-center">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 flex items-center justify-center mb-5 border border-amber-500/20">
                          <Shield className="h-8 w-8 text-amber-400" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-3">investors Only</h3>
                        <p className="text-white/50 text-sm mb-6">
                          This offering is available exclusively to investors 
                          under SEC Regulation D Rule 506(c).
                        </p>
                        <div className="pt-6 border-t border-white/10">
                          <p className="text-white/30 text-xs">
                            investor status will be verified prior to 
                            acceptance of any subscription.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== BEGIN YOUR JOURNEY CTA ==================== */}
          <section className="py-20 lg:py-28 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 lg:p-12 border border-white/10 relative overflow-hidden"
              >
                {/* Background accents */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Content */}
                    <div>
                      <motion.span 
                        className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-4 block"
                        variants={itemVariants}
                      >
                        Get Started
                      </motion.span>
                      <motion.h2 
                        className="text-2xl lg:text-3xl font-bold text-white mb-5"
                        variants={itemVariants}
                      >
                        Begin Your Investment Journey
                      </motion.h2>
                      <motion.p 
                        className="text-white/55 leading-relaxed mb-8"
                        variants={itemVariants}
                      >
                        Ready to explore institutional-quality real estate investments? 
                        Schedule a consultation with our team to discuss how ASAP Capital 
                        aligns with your portfolio objectives.
                      </motion.p>

                      <motion.div 
                        className="flex flex-wrap gap-4"
                        variants={itemVariants}
                      >
                        <Link to="/contact">
                          <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                            <Calendar className="h-4 w-4" />
                            Schedule Consultation
                          </button>
                        </Link>
                        <Link to="/contact">
                          <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                            <Mail className="h-4 w-4" />
                            Request Materials
                          </button>
                        </Link>
                      </motion.div>
                    </div>

                    {/* Contact options */}
                    <motion.div 
                      className="space-y-4"
                      variants={itemVariants}
                    >
                      <div className="bg-[#0A1628]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/20 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                            <Calendar className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold group-hover:text-[#D4AF37] transition-colors">Schedule a Call</h4>
                            <p className="text-white/40 text-sm">Book a time with our team</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-[#0A1628]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/20 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                            <Mail className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold group-hover:text-[#D4AF37] transition-colors">Email Us</h4>
                            <a href="mailto:info@asapcapital.net" className="text-white/40 text-sm hover:text-[#D4AF37] transition-colors">
                              info@asapcapital.net
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="bg-[#0A1628]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/20 transition-all group">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                            <Phone className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h4 className="text-white font-semibold group-hover:text-[#D4AF37] transition-colors">Call Direct</h4>
                            <a href="tel:+1-346-310-0995" className="text-white/40 text-sm hover:text-[#D4AF37] transition-colors">
                              +1 (346) 310-0995
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ADDITIONAL LINKS ==================== */}
          <section className="py-16 relative bg-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
              >
                <motion.p 
                  className="text-center text-white/40 text-sm mb-6"
                  variants={itemVariants}
                >
                  Learn more about our fund
                </motion.p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Link to="/strategy">
                    <motion.div 
                      className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/30 transition-all group flex items-center justify-between"
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-center gap-3">
                        <Target className="h-5 w-5 text-[#D4AF37]" />
                        <span className="text-white group-hover:text-[#D4AF37] transition-colors">Investment Strategy</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                    </motion.div>
                  </Link>
                  
                  <Link to="/fund-terms">
                    <motion.div 
                      className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/30 transition-all group flex items-center justify-between"
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-[#D4AF37]" />
                        <span className="text-white group-hover:text-[#D4AF37] transition-colors">Fund Terms</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                    </motion.div>
                  </Link>
                  
                  <Link to="/portfolio">
                    <motion.div 
                      className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10 hover:border-[#D4AF37]/30 transition-all group flex items-center justify-between"
                      whileHover={{ x: 5 }}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="h-5 w-5 text-[#D4AF37]" />
                        <span className="text-white group-hover:text-[#D4AF37] transition-colors">View Portfolio</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                    </motion.div>
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default InvestmentProcessPage;
